/**
 * MSG-7: обёртка боевых ручек /api/v1/msg для веб-ЛК владельца.
 * Контракт вызовов — зеркало эталона `tools/msg5-harness/node/api.mjs`
 * (MsgApi): olmClient использует ТЕ ЖЕ имена методов и формы ответов.
 *
 * Отличия от эталона — только транспорт:
 * - REST идёт через axiosInstance ЛК: интерсептор сам обновляет протухший
 *   access-токен по refresh-cookie (урок MSG-6 №4 — 401 не терминален);
 * - SSE остаётся на fetch (axios не отдаёт поток), токен читается из
 *   localStorage на каждое ПОДКЛЮЧЕНИЕ; 401 потока лечится «тычком» в REST
 *   (poke) — интерсептор обновит токен — и реконнектом у вызывающего.
 *
 * X-Device-Id обязателен на каждом msg-вызове (модель MSG-3): без него
 * сервер не знает, КАКОЕ криптоустройство владельца говорит.
 */
import axiosInstance, { BASE_API_URL } from '../utils/router/axiosInstance';

export class MsgApiError extends Error {
  constructor(status, detail, path) {
    super(`HTTP ${status} ${path}: ${JSON.stringify(detail)}`);
    this.status = status;
    this.detail = detail;
    this.path = path;
  }
}

function toMsgError(e, path) {
  if (e?.response) {
    return new MsgApiError(e.response.status, e.response.data?.detail ?? e.response.data, path);
  }
  return e; // сеть/обрыв — не HTTP-ошибка, вызывающий ретраит
}

/**
 * Потоковый парсер кадров SSE §5.1 (вынесен из openStream ради unit-тестов):
 * кадры разделяются пустой строкой, `: ping` — комментарий. Возвращает
 * feed(chunk); недоеденный хвост живёт между вызовами.
 *
 * Концы строк нормализуются к \n (ревью р.1, Medium): спека SSE разрешает
 * CR, LF и CRLF — наш бэкенд шлёт LF, но парсер обязан пережить смену
 * сервера/прокси. Хвостовой \r чанка придерживается до следующего куска:
 * его парой может оказаться \n на границе чанков.
 */
export function createSseFeeder({ onReady = () => {}, onNotify = () => {}, onPing = () => {} }) {
  let buf = '';
  let heldCR = false;
  return (chunk) => {
    let s = (heldCR ? '\r' : '') + chunk;
    heldCR = s.endsWith('\r');
    if (heldCR) s = s.slice(0, -1);
    buf += s.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    let idx;
    while ((idx = buf.indexOf('\n\n')) >= 0) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      if (frame.startsWith(':')) { onPing(); continue; }
      const event = /^event: (.*)$/m.exec(frame)?.[1];
      const data = /^data: (.*)$/m.exec(frame)?.[1];
      if (event === 'ready') { onReady(); continue; }
      if (data !== undefined) onNotify(JSON.parse(data));
    }
  };
}

/**
 * Транспорт сессии ЛК владельца: axios с интерсептором (refresh-cookie) и
 * access-токен в localStorage. Вынесен, чтобы участник по приглашению мог
 * ходить теми же ручками со СВОИМ member-JWT (QR-3), не таща сессию ЛК.
 */
export const ownerTransport = {
  // ATT: пятым аргументом — сырые опции axios (`responseType`, ход загрузки).
  // Вложение это БАЙТЫ, а не JSON: без них ответ на скачивание пришёл бы
  // строкой, и расшифровка получила бы искажённый шифротекст.
  // 🔴 headers из opts СЛИВАЮТСЯ с базовыми, а не заменяют их (ревью ATT С2
  // р.1, High): спред opts после headers молча стирал бы X-Device-Id — а в
  // транспорте участника тем же движением терялся Authorization.
  request: (method, path, body, headers, opts = null) => axiosInstance
    .request({
      method, url: path, data: body,
      ...(opts || {}),
      headers: { ...headers, ...(opts?.headers || {}) },
    })
    .then(({ data }) => data),
  /** «Тычок» в REST: 401 заставит интерсептор обновить access-токен. */
  poke: (headers) => axiosInstance
    .request({ method: 'get', url: 'website/me', headers })
    .then(({ data }) => data),
  streamToken: () => localStorage.getItem('access_token'),
};

export class MsgApi {
  /**
   * @param did X-Device-Id браузера (browserDeviceId())
   * @param transport источник авторизации; по умолчанию — сессия ЛК владельца
   */
  constructor(did, transport = ownerTransport) {
    // 🔴 Явный null дефолт параметра НЕ включает (он ловит только undefined),
    // и клиент молча оставался бы без транспорта — падая лишь при первом
    // запросе и совсем не там, где ошибка (ревью WEB-24, High). Падать здесь
    // и сразу. Подставлять `ownerTransport` тоже нельзя: участник ушёл бы в
    // сеть с сессией владельца.
    // Проверяем именно ПУСТОТУ, а не наличие `request`: поток событий
    // обходится одним `streamToken`, и такой транспорт законен.
    if (!transport) {
      throw new Error('MsgApi: транспорт обязателен');
    }
    this.did = did;
    this.transport = transport;
  }

  _headers() {
    return { 'X-Device-Id': this.did };
  }

  async _req(method, path, body, opts = null) {
    try {
      return await this.transport.request(method, path, body, this._headers(), opts);
    } catch (e) {
      throw toMsgError(e, path);
    }
  }

  // --- ATT: вложения ---------------------------------------------------------
  // Тело загрузки — СЫРЫЕ БАЙТЫ шифротекста, ответ скачивания — тоже байты.
  // Метаданные (имя, тип, ключ) сюда не едут вовсе: они внутри E2E-сообщения,
  // сервер их не видит и видеть не должен.

  // --- WEB-24: уведомления о новых сообщениях (Web Push) ---------------------

  /** Публичный ключ VAPID — им браузер подписывается у своего push-сервиса.
   *  404 = уведомления на этом сервере не настроены (флаг или ключи). */
  pushKey() { return this._req('get', 'msg/push/key'); }

  /** @param subscription `{endpoint, keys:{p256dh, auth}}` — РОВНО эти поля.
   *  🔴 `PushSubscription.toJSON()` даёт ещё `expirationTime`, а модель на
   *  сервере — `extra="forbid"`: отправив объект целиком, получишь 422. */
  pushSubscribe(subscription) {
    return this._req('post', 'msg/push/subscribe', subscription);
  }

  /** Выключить уведомления на этом устройстве. Идемпотентно. */
  pushUnsubscribe() { return this._req('post', 'msg/push/unsubscribe'); }

  /** Лимиты вложений — спросить ДО шифрования 25 МБ ради отказа в конце. */
  attachmentQuota() { return this._req('get', 'msg/attachments/quota'); }

  /** @param ciphertext ArrayBuffer шифротекста. @returns {attachment_id, sha256, ...}
   *  @param opts сырые опции axios (onUploadProgress — ход загрузки 25 МБ).
   *  Content-Type едет в opts.headers и ДОЛИВАЕТСЯ к базовым заголовкам
   *  транспортом — Authorization и X-Device-Id остаются на месте. */
  uploadAttachment(ciphertext, opts = null) {
    return this._req('post', 'msg/attachments', ciphertext, {
      ...(opts || {}),
      headers: {
        'Content-Type': 'application/octet-stream', ...(opts?.headers || {}),
      },
    });
  }

  /** @returns ArrayBuffer шифротекста; сверку sha256 делает вызывающий. */
  downloadAttachment(attachmentId) {
    return this._req(
      'get', `msg/attachments/${encodeURIComponent(attachmentId)}`, undefined,
      { responseType: 'arraybuffer' },
    );
  }

  putDevice(body) { return this._req('put', 'msg/devices', body); }
  /** Д-20: какое устройство принципала активно сейчас (до регистрации). */
  myDevice() { return this._req('get', 'msg/devices/mine'); }
  postPrekeys(body) { return this._req('post', 'msg/prekeys', body); }
  peerBundle(conversationId) {
    return this._req('get', `msg/devices/peer?conversation_id=${conversationId}`);
  }
  ensureConversation(body = {}) { return this._req('post', 'msg/conversations', body); }
  postEnvelope(body) { return this._req('post', 'msg/envelopes', body); }
  catchUp(conversationId, afterSeq = 0, limit = 100) {
    return this._req(
      'get',
      `msg/envelopes?conversation_id=${conversationId}&after_seq=${afterSeq}&limit=${limit}`,
    );
  }
  postCursor(body) { return this._req('post', 'msg/cursors', body); }

  // --- QR-2: общая комната ---------------------------------------------------
  // Девять ручек `msg/rooms`. Транспорт, заголовки и разбор ошибок — те же,
  // что у 1:1: комнате нужна не своя инфраструктура, а свой контроллер.
  // 🔴 id комнаты — UUID из ответа сервера, но в путь он идёт через
  // encodeURIComponent: подставлять в URL значение, пришедшее по сети, без
  // экранирования — привычка, которая однажды обходится дорого.

  /** Комнаты, где я состою. v1: ноль или одна. */
  listRooms() { return this._req('get', 'msg/rooms'); }

  /** Ленивое рождение своей комнаты (только владелец). */
  ensureRoom() { return this._req('post', 'msg/rooms', {}); }

  /**
   * Переименование комнаты и/или СВОЕЙ подписи в ней — только владельцем
   * (участнику сервер отвечает 403).
   *
   * Тело: `{title?, display_name?}`, хотя бы одно поле. Пустое сервер отбивает
   * 422 намеренно: запрос, который заведомо ничего не меняет, — опечатка
   * клиента, а не «ничего страшного».
   */
  renameRoom(roomId, body) {
    return this._req('patch', `msg/rooms/${encodeURIComponent(roomId)}`, body);
  }

  /**
   * Участник меняет СОБСТВЕННУЮ подпись (07.09): сервер правит её во всех
   * трёх копиях разом — личность, ростер комнаты, снимок переписки 1:1.
   * Владельцу ручка отвечает 403: его путь — переименование комнаты.
   */
  renameProfile(displayName) {
    return this._req('patch', 'msg/profile', { display_name: displayName });
  }

  /** Ростер: кто в комнате, с именами на момент входа. */
  roomMembers(roomId) {
    return this._req('get', `msg/rooms/${encodeURIComponent(roomId)}/members`);
  }

  /**
   * ОБЯЗАТЕЛЬНЫЙ состав fan-out: устройства-получатели без one-time ключей.
   * Своего устройства в списке нет — сервер его исключает сам.
   */
  roomDevices(roomId) {
    return this._req('get', `msg/rooms/${encodeURIComponent(roomId)}/devices`);
  }

  /**
   * One-time/fallback ключи для ЯВНО перечисленных устройств.
   *
   * 🔴 Пустой список сюда приносить нельзя: `device_ids` объявлен
   * `min_length=1`, и сервер ответит 422. Падаем ЗДЕСЬ и громко — вызов с
   * пустым списком означает ошибку в контроллере (нет устройств без сессии —
   * нет и запроса), а не ситуацию, которую надо обработать по сети.
   */
  claimRoomKeys(roomId, deviceIds) {
    if (!Array.isArray(deviceIds) || deviceIds.length === 0) {
      throw new Error(
        'claimRoomKeys без устройств: сервер ответит 422 (device_ids min_length=1). '
        + 'Не вызывайте claim, если все получатели уже имеют Olm-сессию.',
      );
    }
    return this._req(
      'post', `msg/rooms/${encodeURIComponent(roomId)}/claim`,
      { device_ids: deviceIds },
    );
  }

  /**
   * Пачка копий ОДНОГО логического сообщения: {v, message_id, sent_at, copies}.
   * Принимается целиком или никак; повтор с тем же message_id вернёт прежний
   * room_seq и duplicate=true.
   */
  postRoomEnvelopes(roomId, body) {
    return this._req(
      'post', `msg/rooms/${encodeURIComponent(roomId)}/envelopes`, body,
    );
  }

  /**
   * Догон: страница копий, адресованных МОЕМУ устройству, плюс latest_seq —
   * последний номер КОМНАТЫ. Ряд номеров устройства разрежен, поэтому конец
   * догона определяется latest_seq, а не пустотой страницы.
   */
  roomCatchUp(roomId, afterSeq = 0, limit = 100) {
    return this._req(
      'get',
      `msg/rooms/${encodeURIComponent(roomId)}/envelopes`
      + `?after_seq=${afterSeq}&limit=${limit}`,
    );
  }

  /** Delivered-курсор на пару (комната, УСТРОЙСТВО), а не на принципала. */
  postRoomCursor(roomId, appliedSeq) {
    return this._req(
      'post', `msg/rooms/${encodeURIComponent(roomId)}/cursors`,
      { applied_seq: appliedSeq },
    );
  }

  /** «Тычок» в REST: заставляет транспорт обновить протухший токен. */
  async poke() {
    try {
      return await this.transport.poke(this._headers());
    } catch (e) {
      throw toMsgError(e, 'poke');
    }
  }

  /**
   * SSE-поток §5.1 (структура — эталон api.mjs): ready/closed/close().
   * Токен свежий на момент подключения; протухание ВО ВРЕМЯ потока сервер
   * не проверяет посреди соединения — лечится реконнектом.
   */
  openStream({ onNotify = () => {}, onPing = () => {} } = {}) {
    const ctrl = new AbortController();
    let readySettled = false;
    let readyResolve, readyReject;
    const ready = new Promise((res, rej) => {
      readyResolve = () => { readySettled = true; res(); };
      readyReject = (e) => { readySettled = true; rej(e); };
    });

    const closed = (async () => {
      let streamErr = null;
      try {
        const token = await this.transport.streamToken();
        // INF-2: тот же источник хоста, что у axiosInstance (пусто = same-origin)
        const res = await fetch(`${BASE_API_URL}/api/v1/msg/stream`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...this._headers(),
          },
          signal: ctrl.signal,
        });
        if (!res.ok) {
          let detail = null;
          try { detail = (await res.json())?.detail; } catch { /* тело не JSON */ }
          throw new MsgApiError(res.status, detail, '/msg/stream');
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        const feed = createSseFeeder({ onReady: readyResolve, onNotify, onPing });
        for (;;) {
          const { done, value } = await reader.read();
          if (done) { feed(decoder.decode()); break; }
          feed(decoder.decode(value, { stream: true }));
        }
      } catch (e) {
        streamErr = e;
        if (e.name !== 'AbortError') throw e;
      } finally {
        // Обрыв/EOF/abort ДО ready (находка ревью MSG-5): closed резолвится
        // штатно, но ready обязан отдать честную ошибку, не висеть.
        if (!readySettled) {
          readyReject(streamErr ?? new Error('SSE закрылся до кадра ready'));
        }
      }
    })();
    closed.catch((e) => { if (!readySettled) readyReject(e); });

    return { ready, closed, close: () => ctrl.abort() };
  }
}
